import React from 'react';
import { gql } from '@soundxyz/gql-string';
import type { Maybe } from '@soundxyz/utils';
import { NotificationUser } from '@components/Notifications/NotificationUser';
import {
  contentForPost,
  getContentSnapshot,
  getReleaseTitleSnapshot,
  MobileUsername,
} from '@components/Notifications/shared';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { NotificationPostContentFragmentDoc } from '@graphql/reactQuery';
import { type FragmentType, getFragment, PostSingleUserFragmentDoc } from '@graphql/reactQuery';
import { SPACE } from '@utils/constants';
import { exhaustiveCheck } from '@utils/helpers';
import { webappUriPath } from '@utils/url';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment PostSingleUser on User {
    id
    webappUri
    username
    artist {
      id
    }
    ...NotificationUser
    ...SingleUserNotificationInfo
  }

  fragment NotificationPostContent on Post {
    __typename
    id
    webappUri
    content
    lexicalContent
    ... on ActivityFeedGroupBackingPost {
      activityFeedGroup {
        information {
          __typename
          ... on ReleaseDroppedAggregate {
            release {
              title
              artist {
                name
              }
            }
          }
          ... on UserCollectedManySongsAggregate {
            collectedReleases {
              release {
                title
                artist {
                  name
                }
              }
            }
          }
        }
      }
    }
  }
`);

type Props = {
  postWebappUri: string;
  userFrag: FragmentType<PostSingleUserFragmentDoc>;
  postContentFrag: FragmentType<NotificationPostContentFragmentDoc>;
} & ( // Use for upvoted posts
  | { action: 'upvoted post' }
  | { action: 'reposted' }
  // Use for upvoted post comments
  | { action: 'upvoted reply'; commentContent: Maybe<string> }
  // Use for Posts
  | { action: 'commented post'; commentContent: string }
  // Use for Post Comments under Release
  | {
      action: 'commented release';
      commentContent: string;
      release: {
        title: string;
        webappUri: string;
      };
    }
  // Use for Replies (Post Comment under Post Comment)
  | {
      action: 'commented reply';
      commentContent: string;
      parentCommentContent: string;
    }
);

function renderContent(props: Props) {
  const postContent = contentForPost({
    postContentFrag: getFragment(NotificationPostContentFragmentDoc, props.postContentFrag),
    includeYourPrefix: props.action !== 'reposted',
  });

  const comment =
    'commentContent' in props && props.commentContent != null
      ? getContentSnapshot({ content: props.commentContent })
      : null;

  switch (props.action) {
    case 'reposted': {
      return (
        <>
          <span>reposted</span>
          {SPACE}
          {postContent}
        </>
      );
    }
    case 'upvoted post':
      return (
        <>
          <span>upvoted on</span>
          {SPACE}
          {postContent}
        </>
      );
    case 'upvoted reply':
      return (
        <>
          <span>upvoted your reply</span>
          {SPACE}
          {comment}
        </>
      );
    case 'commented post':
      return (
        <>
          <span>commented</span>
          {SPACE}
          {comment}
          {SPACE}
          <span>on</span>
          {SPACE}
          {postContent}
        </>
      );
    case 'commented release':
      const releaseTitle = getReleaseTitleSnapshot(props.release.title);
      const releasePath = webappUriPath(props.release.webappUri);

      return (
        <>
          <span>commented</span>
          {SPACE}
          {comment}
          {SPACE}
          <span>on</span>
          {SPACE}
          <span className="overflow-hidden text-ellipsis whitespace-nowrap font-semibold">
            <AccessibleLink pathnameUri={releasePath}>{releaseTitle}</AccessibleLink>
          </span>
        </>
      );
    case 'commented reply':
      const parentComment = getContentSnapshot({ content: props.parentCommentContent });

      return (
        <>
          <span>replied</span>
          {SPACE}
          {comment}
          {SPACE}
          <span>to your comment</span>
          {SPACE}
          {parentComment}
        </>
      );

    default: {
      exhaustiveCheck(props);
      return null;
    }
  }
}

export const PostSingleUser = (props: Props) => {
  const { postWebappUri, userFrag } = props;

  const userData = getFragment(PostSingleUserFragmentDoc, userFrag);
  const { webappUri: userWebAppUri, username, artist } = userData;

  const isArtist = !!artist?.id;
  const userURI = webappUriPath(userWebAppUri);

  const postActionContent = React.useMemo(() => {
    return renderContent(props);
  }, [props]);

  return (
    <AccessibleLink
      className="flex w-full min-w-0 items-start sm:items-center md:min-w-[unset]"
      pathnameUri={webappUriPath(postWebappUri)}
    >
      <div className="flex w-full items-start gap-2 pr-2 sm:items-center md:max-w-none md:gap-3 md:pr-3 lt:max-w-[calc(100%-80px)] dt:max-w-none dt:gap-0">
        <AccessibleLink pathnameUri={userURI}>
          <NotificationUser userFrag={userData} disableLink />
        </AccessibleLink>
        <div className="inline-flex min-w-0 flex-wrap items-start dt:flex dt:flex-nowrap">
          <AccessibleLink pathnameUri={userURI} className="z-above2">
            <MobileUsername username={username} isArtist={isArtist} />
          </AccessibleLink>
          {SPACE}
          {postActionContent}
        </div>
      </div>
    </AccessibleLink>
  );
};
